export type TwCompileFn = (css: string, candidates: string[]) => Promise<string>;

export function injectSheet(css: string): void {
  let el = document.getElementById('cf-tailwind') as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = 'cf-tailwind';
    document.head.appendChild(el);
  }
  el.textContent = css;
}

/**
 * Base tokens the vendored shadcn `@/ui` components reference (bg-card,
 * text-muted-foreground, border-input, primary-foreground, …). Registered as a
 * base `@theme` so the utilities compile + their CSS vars emit. Deliberately
 * does NOT redefine tokens the app/themes already own (--color-primary [themes],
 * --color-accent + --color-border [app chrome]) — those resolve to existing
 * values. The per-theme themeBlock is concatenated AFTER this, so an active
 * theme still overrides any overlap (e.g. brand primary). Dark values apply
 * under the app's `.dark` class. The scoped @layer base rule gives a bare
 * `border` the subtle app border color (utilities still override it).
 */
const SHADCN_BASE = `@theme {
  --color-background: #ffffff;
  --color-foreground: #0a0a0a;
  --color-card: #ffffff;
  --color-card-foreground: #0a0a0a;
  --color-popover: #ffffff;
  --color-popover-foreground: #0a0a0a;
  --color-primary-foreground: #fafafa;
  --color-secondary: #f4f4f5;
  --color-secondary-foreground: #18181b;
  --color-muted: #f4f4f5;
  --color-muted-foreground: #71717a;
  --color-accent-foreground: #18181b;
  --color-destructive: #ef4444;
  --color-destructive-foreground: #fafafa;
  --color-input: #e4e4e7;
  --color-ring: #a1a1aa;
}
.dark {
  --color-background: #0a0a0a;
  --color-foreground: #fafafa;
  --color-card: #161616;
  --color-card-foreground: #fafafa;
  --color-popover: #161616;
  --color-popover-foreground: #fafafa;
  --color-primary-foreground: #18181b;
  --color-secondary: #27272a;
  --color-secondary-foreground: #fafafa;
  --color-muted: #27272a;
  --color-muted-foreground: #a1a1aa;
  --color-accent-foreground: #fafafa;
  --color-destructive: #7f1d1d;
  --color-destructive-foreground: #fafafa;
  --color-input: #27272a;
  --color-ring: #52525b;
}
@layer base {
  .codeframe-content *,
  .codeframe-content ::before,
  .codeframe-content ::after { border-color: var(--color-border); }
}`;

export function makeCompiler(compileFn: TwCompileFn) {
  return {
    async compile(input: { themeBlock: string; scopeBlocks: string; classes: Set<string> }) {
      const css = `@import "tailwindcss";\n${SHADCN_BASE}\n${input.themeBlock}\n${input.scopeBlocks}`;
      const out = await compileFn(css, [...input.classes]);
      injectSheet(out);
    },
  };
}

/** Real compileFn — browser only. Uses the tailwindcss core programmatic API
 *  (`compile` from lib.mjs). `@import "tailwindcss"` is resolved to the
 *  framework's self-contained index.css (theme + preflight + @tailwind
 *  utilities, no further @imports) via Vite's `?raw` loader. Lazy-imported so
 *  the heavy core never loads under vitest. */
export async function browserCompileFn(css: string, candidates: string[]): Promise<string> {
  const [{ compile }, { default: frameworkCss }] = await Promise.all([
    import('tailwindcss'),
    import('tailwindcss/index.css?raw'),
  ]);
  const compiler = await compile(css, {
    base: '/',
    loadStylesheet: async (id: string, base: string) => ({ path: id, base, content: frameworkCss }),
  });
  return compiler.build(candidates);
}
